import type React from "react";
import { forwardRef, useEffect, useMemo, useState, type ReactElement, type Ref } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import type { TransitionProps } from "@mui/material/transitions";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import IconButton from "@mui/material/IconButton";
import CommentIcon from "@mui/icons-material/Comment";
import EmailIcon from "@mui/icons-material/Email";
import EventIcon from "@mui/icons-material/Event";
import HomeIcon from "@mui/icons-material/Home";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaymentIcon from "@mui/icons-material/Payment";
import PersonIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { CheckCircleIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import AvailableSlots from "../../components/AvailableSlots";
import AdminBookingCard from "../../components/dashboard/AdminBookingCard";
import AdminBookingDraftFields from "../../components/dashboard/AdminBookingDraftFields";
import { manageUsersQueryOptions } from "../../queryOptions/ManageBookingsOptions";
import { Membership } from "../../schemas/memberships";
import { User } from "../../schemas/user";
import { getBookingEventNotices } from "../../utils/facilityEvents";
import {
  type BookingDraft,
  type LaneOption,
  buildAdminBookingPayload,
  calculateDraftSubtotal,
  calculateDraftTax,
  copyDayFromSource,
  copyTimeFromSource,
  createEmptyDraft,
  draftsOverlapEachOther,
  formatCurrency,
  formatDateToYYYYMMDD,
  formatDuration,
  getDatesToCheck,
  getDraftValidationError,
  getFormattedDraftTime,
  getHourlyPrice,
  getTotalDuration,
  isDraftComplete,
  roundMoney,
  siblingSlotsForDraft,
} from "./bookingDraftUtils";

const MobileDialogTransition = forwardRef(function MobileDialogTransition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface AddBookingFormProps {
  open: boolean;
  onClose: () => void;
  onAddBooking: (bookingData: any) => Promise<any>;
  onBatchComplete?: (count: number, options?: { partial?: boolean }) => void;
  lanes: LaneOption[];
}

const getPaymentMethodDisplayName = (method: string) => {
  switch (method) {
    case "pay_at_counter":
      return "PAY_AT_COUNTER";
    case "send_invoice":
      return "SEND_INVOICE";
    case "group_on":
      return "GROUP_ON";
    default:
      return method.toUpperCase();
  }
};

const AddBookingForm: React.FC<AddBookingFormProps> = ({
  open,
  onClose,
  onAddBooking,
  onBatchComplete,
  lanes,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [membershipStatus, setMembershipStatus] = useState<Membership | undefined>(undefined);
  const { data, refetch } = useQuery(manageUsersQueryOptions());
  const userDetails = data?.filter((user: User) => user.role === "user");
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const [drafts, setDrafts] = useState<BookingDraft[]>([]);
  const [paymentMethod, setPaymentMethod] = useState("pay_at_counter");
  const [taxRate, setTaxRate] = useState(14);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ current: 0, total: 0 });
  const [checkingIds, setCheckingIds] = useState<string[]>([]);

  const handleUserSelect = (_event: any, newValue: any) => {
    setSelectedUser(newValue);
    if (newValue) {
      const user = data?.find((item: any) => item.id === newValue.id);
      if (user) {
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setEmail(user.email);
        setPhone(user.phoneNumber);
        setAddress(user.address);
        setMembershipStatus(user.membershipStatus);
      }
    } else {
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setAddress("");
      setSelectedUser("");
      setMembershipStatus(undefined);
    }
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setSpecialRequests("");
    setSelectedUser(null);
    setMembershipStatus(undefined);
    setDrafts([]);
    setPaymentMethod("pay_at_counter");
    setTaxRate(14);
    setDiscountPercent(0);
    setError(null);
    setIsSubmitting(false);
    setSubmitProgress({ current: 0, total: 0 });
    setCheckingIds([]);
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const updateDraft = (id: string, updates: Partial<BookingDraft>) => {
    setDrafts((prev) => {
      const next = prev.map((draft) =>
        draft.id === id ? { ...draft, ...updates } : draft
      );
      const schedulingKeys = [
        "selectedLaneId",
        "dateSelectionType",
        "selectedDate",
        "selectedDates",
        "dateRange",
        "startTime",
        "endTime",
        "useIndividualTimes",
        "individualDateTimes",
        "sameDay",
        "sameTime",
      ];
      const shouldSync = Object.keys(updates).some((key) =>
        schedulingKeys.includes(key)
      );
      if (!shouldSync) return next;
      const source = next[0];
      if (!source) return next;
      return next.map((draft, index) => {
        if (index === 0) return draft;
        let updated = draft;
        if (draft.sameDay) updated = copyDayFromSource(updated, source);
        if (draft.sameTime) updated = copyTimeFromSource(updated, source);
        return updated;
      });
    });
  };

  const handleAddDraft = () => {
    setError(null);
    setDrafts((prev) => [
      ...prev.map((draft) => ({ ...draft, expanded: false })),
      createEmptyDraft(true),
    ]);
  };

  const expandDraft = (id: string) => {
    setDrafts((prev) =>
      prev.map((draft) => ({ ...draft, expanded: draft.id === id }))
    );
  };

  const collapseDraft = (id: string) => {
    setDrafts((prev) =>
      prev.map((draft) =>
        draft.id === id ? { ...draft, expanded: false } : draft
      )
    );
  };

  const handleRemoveDraft = (id: string) => {
    setDrafts((prev) => {
      const removedWasFirst = prev[0]?.id === id;
      const filtered = prev.filter((draft) => draft.id !== id);
      if (filtered.length === 0) return filtered;
      if (removedWasFirst) {
        return filtered.map((draft, index) => ({
          ...draft,
          sameDay: false,
          sameTime: false,
          expanded: index === 0,
        }));
      }
      if (filtered.some((draft) => draft.expanded)) return filtered;
      return filtered.map((draft, index) => ({
        ...draft,
        expanded: index === filtered.length - 1,
      }));
    });
  };

  const toggleSameDay = (id: string, checked: boolean) => {
    setDrafts((prev) => {
      const source = prev[0];
      return prev.map((draft, index) => {
        if (draft.id !== id || index === 0) return draft;
        if (!checked) return { ...draft, sameDay: false };
        return { ...copyDayFromSource(draft, source), sameDay: true };
      });
    });
  };

  const toggleSameTime = (id: string, checked: boolean) => {
    setDrafts((prev) => {
      const source = prev[0];
      return prev.map((draft, index) => {
        if (draft.id !== id || index === 0) return draft;
        if (!checked) return { ...draft, sameTime: false };
        return { ...copyTimeFromSource({ ...draft, sameTime: true }, source), sameTime: true };
      });
    });
  };

  const scheduleSignature = drafts
    .map(
      (draft) =>
        `${draft.id}|${draft.selectedLaneId}|${draft.dateSelectionType}|${draft.startTime}|${draft.endTime}|${getDatesToCheck(draft).join(",")}|${JSON.stringify(draft.individualDateTimes)}|${draft.useIndividualTimes}`
    )
    .join("||");

  const siblingSlotsById = useMemo(() => {
    const map: Record<string, any[]> = {};
    drafts.forEach((draft) => {
      map[draft.id] = siblingSlotsForDraft(draft, drafts, lanes);
    });
    return map;
    // scheduleSignature captures the booking-relevant fields used by sibling overlap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSignature, lanes]);

  const expandedDraft = drafts.find((draft) => draft.expanded);
  const completeDrafts = drafts.filter(isDraftComplete);

  const totals = useMemo(() => {
    const duration = completeDrafts.reduce(
      (sum, draft) => sum + getTotalDuration(draft),
      0
    );
    const subtotal = completeDrafts.reduce(
      (sum, draft) => sum + calculateDraftSubtotal(draft, lanes, discountPercent),
      0
    );
    const tax = completeDrafts.reduce(
      (sum, draft) => sum + calculateDraftTax(draft, lanes, discountPercent, taxRate),
      0
    );
    return {
      duration,
      subtotal: roundMoney(subtotal),
      tax: roundMoney(tax),
      total: roundMoney(subtotal + tax),
    };
  }, [completeDrafts, lanes, discountPercent, taxRate]);

  const taxHolidayNotices = useMemo(() => {
    const seen = new Set<string>();
    return completeDrafts.flatMap((draft) =>
      getDatesToCheck(draft).flatMap((date) =>
        getBookingEventNotices(date).filter((event) => {
          if (!event.waiveBookingTax || seen.has(event.id)) return false;
          seen.add(event.id);
          return true;
        })
      )
    );
  }, [completeDrafts]);

  const validateCustomer = (): string | null => {
    if (!firstName.trim()) return "First name is required";
    if (!lastName.trim()) return "Last name is required";
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    if (!phone.trim()) return "Phone number is required";
    return null;
  };

  const customerValid = !validateCustomer();
  const crossOverlap = draftsOverlapEachOther(drafts, lanes);
  const allDraftsValid =
    drafts.length > 0 &&
    drafts.every((draft) => isDraftComplete(draft) && !draft.hasOverlap) &&
    !crossOverlap;
  const isFormValid = customerValid && allDraftsValid && checkingIds.length === 0;

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const customerError = validateCustomer();
    if (customerError) {
      setError(customerError);
      return;
    }
    if (drafts.length === 0) {
      setError("Please add at least one booking");
      return;
    }
    for (let index = 0; index < drafts.length; index++) {
      const draftError = getDraftValidationError(drafts[index], index);
      if (draftError) {
        setError(draftError);
        return;
      }
    }
    if (crossOverlap) {
      setError(crossOverlap.message);
      return;
    }

    setIsSubmitting(true);
    const payloads = drafts.map((draft) =>
      buildAdminBookingPayload(
        draft,
        lanes,
        {
          name: `${firstName} ${lastName}`,
          email,
          phone,
          address,
        },
        specialRequests,
        paymentMethod,
        taxRate,
        discountPercent
      )
    );

    let created = 0;
    try {
      for (const payload of payloads) {
        setSubmitProgress({ current: created + 1, total: payloads.length });
        await onAddBooking(payload);
        created++;
      }
      await refetch();
      onBatchComplete?.(created);
      resetForm();
      onClose();
    } catch (submitError: any) {
      const message =
        submitError?.response?.data?.message ||
        submitError?.message ||
        "Failed to create booking. Please try again.";
      if (created > 0) {
        setDrafts((prev) =>
          prev.slice(created).map((draft, index) => ({
            ...draft,
            sameDay: false,
            sameTime: false,
            expanded: index === 0,
          }))
        );
        setError(
          `${created} booking(s) saved. Booking ${created + 1} failed: ${message}`
        );
        onBatchComplete?.(created, { partial: true });
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
      setSubmitProgress({ current: 0, total: 0 });
    }
  };

  const laneNames = completeDrafts
    .map((draft) => lanes.find((lane) => lane.id === draft.selectedLaneId)?.name)
    .filter(Boolean)
    .join(", ");

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSubmitting) {
          resetForm();
          onClose();
        }
      }}
      fullWidth
      fullScreen={isMobile}
      maxWidth="lg"
      TransitionComponent={isMobile ? MobileDialogTransition : undefined}
      transitionDuration={isMobile ? { enter: 300, exit: 250 } : undefined}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          overflow: "hidden",
          position: "relative",
          m: { xs: 0, md: 2 },
          maxHeight: { xs: "100%", md: "calc(100% - 32px)" },
        },
      }}
    >
      {isSubmitting && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 1300,
            borderRadius: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {submitProgress.total > 1
              ? `Creating booking ${submitProgress.current} of ${submitProgress.total}...`
              : "Creating Booking..."}
          </Typography>
        </Box>
      )}

      <DialogTitle
        sx={{
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          py: { xs: 1.5, md: 2 },
          px: { xs: 2, md: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography component="span" variant={isMobile ? "h6" : "h5"} fontWeight={700}>
            Add New Booking
          </Typography>
          {isMobile && (
            <IconButton
              aria-label="Close"
              onClick={() => {
                if (!isSubmitting) {
                  resetForm();
                  onClose();
                }
              }}
              sx={{ color: "inherit" }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="flex-start">
            <InfoOutlinedIcon sx={{ mr: 1, mt: 0.25, color: "info.main", flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
              Fill in the customer details and booking information below.
            </Typography>
          </Stack>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mx: { xs: 2, md: 3 }, mb: 2 }}>
            {error}
          </Alert>
        )}

        <Divider />

        <Grid container sx={{ p: { xs: 2, md: 3 } }}>
          <Grid item xs={12} md={6} sx={{ pr: { xs: 0, md: 2 } }}>
            <Typography variant="h6" sx={{ mb: 3, display: "flex", alignItems: "center" }}>
              <PersonIcon sx={{ mr: 1 }} />
              Customer Details
            </Typography>

            <Autocomplete
              disablePortal
              options={userDetails?.map((user: any) => ({
                id: user.id,
                label: `${user.firstName} ${user.lastName} (${user.email})`,
              }))}
              getOptionLabel={(option: any) => option.label || ""}
              value={selectedUser}
              onChange={handleUserSelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Existing User"
                  variant="outlined"
                  fullWidth
                />
              )}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "background.paper",
                },
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                <EmailIcon sx={{ mr: 1, color: "action.active" }} />
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                <PhoneIcon sx={{ mr: 1, color: "action.active" }} />
                <TextField
                  label="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", alignItems: "flex-start" }}>
                <HomeIcon sx={{ mr: 1, mt: 2, color: "action.active" }} />
                <TextField
                  label="Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", alignItems: "flex-start" }}>
                <CommentIcon sx={{ mr: 1, mt: 2, color: "action.active" }} />
                <TextField
                  label="Note"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              {membershipStatus && (
                <Tooltip
                  title="This user has an active membership and is eligible for a discount."
                  placement="top"
                >
                  <Chip
                    label="Active Membership"
                    color="success"
                    size="small"
                    variant="outlined"
                    icon={<CheckCircleIcon />}
                    sx={{ ml: 2, mt: 2 }}
                  />
                </Tooltip>
              )}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center" }}>
                  <PaymentIcon sx={{ mr: 1 }} />
                  Payment Method
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="payment-method-label">Payment Method</InputLabel>
                  <Select
                    labelId="payment-method-label"
                    value={paymentMethod}
                    onChange={(event: SelectChangeEvent) => setPaymentMethod(event.target.value)}
                    label="Payment Method"
                  >
                    <MenuItem value="pay_at_counter">PAY_CASH / PAY_AT_COUNTER</MenuItem>
                    <MenuItem value="send_invoice">SEND_INVOICE</MenuItem>
                    <MenuItem value="confirmed">CONFIRMED</MenuItem>
                    <MenuItem value="group_on">GROUP_ON</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {expandedDraft?.selectedLaneId && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <EventIcon sx={{ mr: 1 }} />
                  Availability Summary
                </Typography>
                <AvailableSlots
                  selectedDate={
                    expandedDraft.dateSelectionType === "single"
                      ? expandedDraft.selectedDate
                        ? formatDateToYYYYMMDD(expandedDraft.selectedDate)
                        : undefined
                      : undefined
                  }
                  selectedDates={
                    expandedDraft.dateSelectionType === "single"
                      ? []
                      : getDatesToCheck(expandedDraft)
                  }
                  startTime={expandedDraft.startTime}
                  endTime={expandedDraft.endTime}
                  listingId={expandedDraft.selectedLaneId}
                  dateSelectionType={expandedDraft.dateSelectionType}
                  individualDateTimes={
                    expandedDraft.dateSelectionType === "multiple" &&
                    expandedDraft.useIndividualTimes
                      ? expandedDraft.individualDateTimes
                      : undefined
                  }
                />
              </Box>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              mt: { xs: 4, md: 0 },
              pl: { xs: 0, md: 2 },
              position: { xs: "static", md: "sticky" },
              top: 20,
              height: "fit-content",
              maxHeight: { xs: "none", md: "calc(100vh - 40px)" },
              overflowY: { xs: "visible", md: "auto" },
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Booking Details
            </Typography>

            {drafts.length === 0 && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddDraft}
                fullWidth={isMobile}
                sx={{ mb: 2 }}
              >
                Add booking
              </Button>
            )}

            {drafts.map((draft, index) => (
              <Box key={draft.id} sx={{ mb: 2 }}>
                {draft.expanded ? (
                  <Paper
                    elevation={2}
                    sx={{ p: { xs: 1.5, md: 2 }, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", sm: "center" }}
                      spacing={1}
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        Booking {index + 1}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ExpandLessIcon />}
                          onClick={() => collapseDraft(draft.id)}
                          sx={{ flex: { xs: 1, sm: "none" } }}
                        >
                          Shrink
                        </Button>
                        {drafts.length > 1 && (
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() => handleRemoveDraft(draft.id)}
                            sx={{ flex: { xs: 1, sm: "none" } }}
                          >
                            Remove
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                    <AdminBookingDraftFields
                      draft={draft}
                      index={index}
                      isFirst={index === 0}
                      lanes={lanes}
                      discountPercent={discountPercent}
                      siblingSlots={siblingSlotsById[draft.id] || []}
                      dateLocked={index > 0 && draft.sameDay}
                      timeLocked={index > 0 && draft.sameTime}
                      onChange={(updates) => updateDraft(draft.id, updates)}
                      onToggleSameDay={(checked) => toggleSameDay(draft.id, checked)}
                      onToggleSameTime={(checked) => toggleSameTime(draft.id, checked)}
                      onCheckingChange={(checking) =>
                        setCheckingIds((ids) =>
                          checking
                            ? Array.from(new Set([...ids, draft.id]))
                            : ids.filter((id) => id !== draft.id)
                        )
                      }
                    />
                  </Paper>
                ) : (
                  <AdminBookingCard
                    draft={draft}
                    index={index}
                    isFirst={index === 0}
                    lanes={lanes}
                    discountPercent={discountPercent}
                    onEdit={() => expandDraft(draft.id)}
                    onRemove={() => handleRemoveDraft(draft.id)}
                    onToggleSameDay={(checked) => toggleSameDay(draft.id, checked)}
                    onToggleSameTime={(checked) => toggleSameTime(draft.id, checked)}
                  />
                )}
              </Box>
            ))}

            {drafts.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddDraft}
                fullWidth={isMobile}
                sx={{ mb: 2 }}
              >
                Add booking
              </Button>
            )}

            {crossOverlap && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {crossOverlap.message}
              </Alert>
            )}
          </Grid>
        </Grid>

        <Divider />

        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "grey.50" }}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "primary.light",
            }}
          >
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Booking Summary
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Customer:
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ wordBreak: "break-word" }}>
                  {firstName && lastName ? `${firstName} ${lastName}` : "..."}
                  {email && ` (${email})`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Time:
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ wordBreak: "break-word" }}>
                  {completeDrafts.length === 0
                    ? "..."
                    : completeDrafts.map((draft) => getFormattedDraftTime(draft)).join(", ")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Lane/Cage:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {laneNames || "..."}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status:
                </Typography>
                <Chip
                  label={getPaymentMethodDisplayName(paymentMethod)}
                  color={paymentMethod === "pay_at_counter" ? "primary" : "secondary"}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Hourly Rate:
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ wordBreak: "break-word" }}>
                  {completeDrafts.length === 0
                    ? "..."
                    : completeDrafts
                        .map((draft) => formatCurrency(getHourlyPrice(draft, lanes)))
                        .join(", ")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Duration:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {completeDrafts.length > 0
                    ? formatDuration(totals.duration)
                    : "No times selected"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {completeDrafts.length > 0
                    ? formatCurrency(totals.subtotal)
                    : "No times selected"}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "flex-end" },
                  gap: 1.5,
                }}
              >
                <TextField
                  label="Discount (%)"
                  type="number"
                  value={discountPercent}
                  onChange={(e) => {
                    const value = Number.parseFloat(e.target.value);
                    setDiscountPercent(isNaN(value) ? 0 : Math.min(100, Math.max(0, value)));
                  }}
                  size="small"
                  sx={{ width: { xs: "100%", sm: "140px" } }}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
                <TextField
                  label="Tax Rate (%)"
                  type="number"
                  value={taxRate}
                  onChange={(e) => {
                    const value = Number.parseFloat(e.target.value);
                    setTaxRate(isNaN(value) ? 0 : value);
                  }}
                  InputProps={{
                    endAdornment: <Typography variant="body2">%</Typography>,
                  }}
                  size="small"
                  sx={{ width: { xs: "100%", sm: "120px" } }}
                  inputProps={{ min: 0, step: 0.1 }}
                />
                <Typography variant="body1" fontWeight="medium">
                  {completeDrafts.length > 0 ? formatCurrency(totals.tax) : "No times selected"}
                </Typography>
              </Grid>
              {taxHolidayNotices.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    {taxHolidayNotices.map((event) => event.bookingLabel).join(" ")}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Total Amount:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {completeDrafts.length > 0 ? formatCurrency(totals.total) : "No times selected"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          bgcolor: "grey.50",
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
        }}
      >
        <Button
          onClick={() => {
            resetForm();
            onClose();
          }}
          color="inherit"
          variant="outlined"
          fullWidth={isMobile}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isFormValid || isSubmitting}
          fullWidth={isMobile}
          sx={{ px: { xs: 2, md: 4 }, whiteSpace: { xs: "normal", sm: "nowrap" } }}
        >
          {drafts.some((draft) => draft.hasOverlap) || crossOverlap
            ? "Time Conflict - Cannot Book"
            : "Create Booking"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBookingForm;
